import _toConsumableArray from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/toConsumableArray.js";
import _defineProperty from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _objectWithoutProperties from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
import _slicedToArray from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
var _excluded = ["state"],
    _excluded2 = ["state"],
    _excluded3 = ["state", "modal", "portal", "hideOnEscape", "hideOnControl", "hideOnHoverOutside", "disablePointerEventsOnApproach"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useRef, useState, useEffect, useContext, useCallback, createContext } from 'react';
import { contains } from 'ariakit-utils/dom';
import { addGlobalEventListener } from 'ariakit-utils/events';
import { hasFocusWithin } from 'ariakit-utils/focus';
import { usePortalRef, useBooleanEvent, useSafeLayoutEffect, useWrapElement, useForkRef, useLiveRef, useEvent } from 'ariakit-utils/hooks';
import { chain } from 'ariakit-utils/misc';
import { createHook, createComponent, createElement } from 'ariakit-utils/system';
import { usePopover } from '../popover/popover.js';
import { jsx } from 'react/jsx-runtime';

function getEventPoint(event) {
  return [event.clientX, event.clientY];
}

function isPointInPolygon(point, polygon) {
  var _point = _slicedToArray(point, 2),
      x = _point[0],
      y = _point[1];

  var isInside = false;
  var length = polygon.length;

  for (var i = 0, j = length - 1; i < length; j = i++) {
    var _ref4 = polygon[i] || [0, 0],
        _ref5 = _slicedToArray(_ref4, 2),
        xi = _ref5[0],
        yi = _ref5[1];

    var _ref6 = polygon[j] || [0, 0],
        _ref7 = _slicedToArray(_ref6, 2),
        xj = _ref7[0],
        yj = _ref7[1];

    var intersect = yi >= y !== yj >= y && x <= (xj - xi) * (y - yi) / (yj - yi) + xi;

    if (intersect) {
      isInside = !isInside;
    }
  }

  return isInside;
}

function getEnterPointPlacement(enterPoint, rect) {
  var top = rect.top,
      right = rect.right,
      bottom = rect.bottom,
      left = rect.left;

  var _enterPoint = _slicedToArray(enterPoint, 2),
      x = _enterPoint[0],
      y = _enterPoint[1];

  var placementX = x < left ? "left" : x > right ? "right" : null;
  var placementY = y < top ? "top" : y > bottom ? "bottom" : null;
  return [placementX, placementY];
}

function getElementPolygon(element, enterPoint) {
  var rect = element.getBoundingClientRect();
  var top = rect.top,
      right = rect.right,
      bottom = rect.bottom,
      left = rect.left;

  var _getEnterPointPlaceme = getEnterPointPlacement(enterPoint, rect),
      _getEnterPointPlaceme2 = _slicedToArray(_getEnterPointPlaceme, 2),
      x = _getEnterPointPlaceme2[0],
      y = _getEnterPointPlaceme2[1];

  var polygon = [enterPoint];

  if (x) {
    if (y !== "top") {
      polygon.push([x === "left" ? left : right, top]);
    }

    polygon.push([x === "left" ? right : left, top]);
    polygon.push([x === "left" ? right : left, bottom]);

    if (y !== "bottom") {
      polygon.push([x === "left" ? left : right, bottom]);
    }
  } else if (y === "top") {
    polygon.push([left, top]);
    polygon.push([left, bottom]);
    polygon.push([right, bottom]);
    polygon.push([right, top]);
  } else {
    polygon.push([left, bottom]);
    polygon.push([left, top]);
    polygon.push([right, top]);
    polygon.push([right, bottom]);
  }

  return polygon;
}

function isMovingOnHovercard(target, card, anchor, nested) {
  // The hovercard element has focus so we should keep it visible.
  if (hasFocusWithin(card)) return true; // The mouse is moving on an element inside the hovercard.

  if (!target) return false;
  if (contains(card, target)) return true; // The mouse is moving on an element inside the anchor element.

  if (anchor && contains(anchor, target)) return true; // The mouse is moving on an element inside a nested hovercard.

  if (nested != null && nested.some(function (card) {
    return isMovingOnHovercard(target, card, anchor);
  })) {
    return true;
  }

  return false;
} // The autoFocusOnShow state will be set to true when the hovercard disclosure
// element is clicked. We have to reset it to false when the hovercard element
// gets hidden or is unmounted.


function useAutoFocusOnShow(_ref) {
  var state = _ref.state,
      props = _objectWithoutProperties(_ref, _excluded);

  var mountedRef = useLiveRef(state.mounted); // Resets autoFocusOnShow

  useEffect(function () {
    if (!state.mounted) {
      state.setAutoFocusOnShow(false);
    }
  }, [state.mounted, state.setAutoFocusOnShow]); // On unmount as well.

  useEffect(function () {
    return function () {
      if (!mountedRef.current) {
        state.setAutoFocusOnShow(false);
      }
    };
  }, [state.setAutoFocusOnShow]);
  return _objectSpread({
    // If the hovercard is modal, we should always autoFocus on show.
    autoFocusOnShow: !!props.modal || state.autoFocusOnShow
  }, props);
} // When the hovercard element has focus, we should move focus back to the anchor
// element when the hovercard gets hidden (or unmounted).


function useAutoFocusOnHide(_ref2) {
  var state = _ref2.state,
      props = _objectWithoutProperties(_ref2, _excluded2);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      autoFocusOnHide = _useState2[0],
      setAutoFocusOnHide = _useState2[1]; // Resets autoFocusOnHide


  useEffect(function () {
    if (!state.mounted) {
      setAutoFocusOnHide(false);
    }
  }, [state.mounted]);
  var onFocusProp = useEvent(props.onFocus);
  var onFocus = useCallback(function (event) {
    onFocusProp(event);
    if (event.defaultPrevented) return;
    setAutoFocusOnHide(true);
  }, [onFocusProp]);
  props = _objectSpread(_objectSpread({
    autoFocusOnHide: autoFocusOnHide,
    finalFocusRef: state.anchorRef
  }, props), {}, {
    onFocus: onFocus
  });
  return props;
}

var NestedHovercardContext = /*#__PURE__*/createContext(null);
/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render a hovercard element, which is a popover that's
 * usually made visible by hovering the mouse cursor over an anchor element
 * (`HovercardAnchor`).
 * @see https://ariakit.org/components/hovercard
 * @example
 * ```jsx
 * const state = useHovercardState();
 * const props = useHovercard({ state });
 * <HovercardAnchor state={state}>@username</HovercardAnchor>
 * <Role {...props}>Details</Role>
 * ```
 */

var useHovercard = createHook(function (_ref3) {
  var state = _ref3.state,
      _ref3$modal = _ref3.modal,
      modal = _ref3$modal === void 0 ? false : _ref3$modal,
      _ref3$portal = _ref3.portal,
      portal = _ref3$portal === void 0 ? !!modal : _ref3$portal,
      _ref3$hideOnEscape = _ref3.hideOnEscape,
      hideOnEscape = _ref3$hideOnEscape === void 0 ? true : _ref3$hideOnEscape,
      _ref3$hideOnControl = _ref3.hideOnControl,
      hideOnControl = _ref3$hideOnControl === void 0 ? false : _ref3$hideOnControl,
      _ref3$hideOnHoverOuts = _ref3.hideOnHoverOutside,
      hideOnHoverOutside = _ref3$hideOnHoverOuts === void 0 ? true : _ref3$hideOnHoverOuts,
      _ref3$disablePointerE = _ref3.disablePointerEventsOnApproach,
      disablePointerEventsOnApproach = _ref3$disablePointerE === void 0 ? !!hideOnHoverOutside : _ref3$disablePointerE,
      props = _objectWithoutProperties(_ref3, _excluded3);

  var ref = useRef(null);

  var _useState3 = useState([]),
      _useState4 = _slicedToArray(_useState3, 2),
      nestedHovercards = _useState4[0],
      setNestedHovercards = _useState4[1];

  var hideTimeoutRef = useRef(0);
  var enterPointRef = useRef(null);

  var _usePortalRef = usePortalRef(portal, props.portalRef),
      portalRef = _usePortalRef.portalRef,
      domReady = _usePortalRef.domReady;

  var hideOnEscapeProp = useBooleanEvent(hideOnEscape);
  var hideOnControlProp = useBooleanEvent(hideOnControl); // Hide on Escape/Control. Popover already handles this, but only when the
  // dialog, the backdrop or the disclosure elements are focused. Since the
  // hovercard, by default, does not receive focus when it's shown, we need to
  // handle this globally here.

  useEffect(function () {
    if (!state.open) return;
    return addGlobalEventListener("keydown", function (event) {
      if (event.defaultPrevented) return;
      var isEscape = event.key === "Escape" && hideOnEscapeProp(event);
      var isControl = event.key === "Control" && hideOnControlProp(event);

      if (isEscape || isControl) {
        state.hide();
      }
    });
  }, [state.open, hideOnEscapeProp, hideOnControlProp, state.hide]);
  var mayHideOnHoverOutside = !!hideOnHoverOutside;
  var hideOnHoverOutsideProp = useBooleanEvent(hideOnHoverOutside);
  var mayDisablePointerEvents = !!disablePointerEventsOnApproach;
  var disablePointerEventsProp = useBooleanEvent(disablePointerEventsOnApproach); // Checks whether the mouse is moving toward the hovercard. If not, hide the
  // card after a short delay (hideTimeout).

  useEffect(function () {
    if (!domReady) return;
    if (!state.mounted) return;
    if (!mayHideOnHoverOutside && !mayDisablePointerEvents) return;
    var element = ref.current;
    if (!element) return;

    var onMouseMove = function onMouseMove(event) {
      var enterPoint = enterPointRef.current;
      var target = event.target;
      var anchor = state.anchorRef.current; // Checks whether the hovercard element has focus or the mouse is moving
      // through valid hovercard elements.

      if (isMovingOnHovercard(target, element, anchor, nestedHovercards)) {
        // While the mouse is moving over the anchor element while the hover
        // card is open, keep track of the mouse position so we'll use the
        // last point before the mouse leaves the anchor element.
        enterPointRef.current = target && anchor && contains(anchor, target) ? getEventPoint(event) : null;
        window.clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = 0;
        return;
      } // If there's already a scheduled timeout to hide the hovercard, we do
      // nothing.


      if (hideTimeoutRef.current) return; // Enter point will be null when the user hovers over the hovercard
      // element.

      if (enterPoint) {
        var currentPoint = getEventPoint(event);
        var polygon = getElementPolygon(element, enterPoint); // If the current's event mouse position is inside the transit
        // polygon, this means that the mouse is moving toward the hover card,
        // so we disable this event. This is necessary because the mousemove
        // event may trigger focus on other elements and close the hovercard.

        if (isPointInPolygon(currentPoint, polygon)) {
          // Refreshes the enter point.
          enterPointRef.current = currentPoint;
          if (!disablePointerEventsProp(event)) return;
          event.preventDefault();
          event.stopPropagation();
          return;
        }
      }

      if (!hideOnHoverOutsideProp(event)) return; // Otherwise, hide the hovercard after a short delay (hideTimeout).

      hideTimeoutRef.current = window.setTimeout(function () {
        hideTimeoutRef.current = 0;
        state.hide();
      }, state.hideTimeout);
    };

    return addGlobalEventListener("mousemove", onMouseMove, true);
  }, [domReady, state.mounted, mayHideOnHoverOutside, mayDisablePointerEvents, state.anchorRef, nestedHovercards, disablePointerEventsProp, hideOnHoverOutsideProp, state.hide, state.hideTimeout]); // Disable mouse events while the mouse is moving toward the hovercard. This
  // is necessary because these events may trigger focus on other elements and
  // close the hovercard while the user is moving the mouse toward it.

  useEffect(function () {
    if (!domReady) return;
    if (!state.mounted) return;
    if (!mayDisablePointerEvents) return;

    var disableEvent = function disableEvent(event) {
      var element = ref.current;
      if (!element) return;
      var enterPoint = enterPointRef.current;
      if (!enterPoint) return;
      var polygon = getElementPolygon(element, enterPoint);

      if (isPointInPolygon(getEventPoint(event), polygon)) {
        if (!disablePointerEventsProp(event)) return;
        event.preventDefault();
        event.stopPropagation();
      }
    };

    return chain( // Note: we may need to add pointer events here in the future.
    addGlobalEventListener("mouseenter", disableEvent, true), addGlobalEventListener("mouseover", disableEvent, true), addGlobalEventListener("mouseout", disableEvent, true), addGlobalEventListener("mouseleave", disableEvent, true));
  }, [domReady, state.mounted, mayDisablePointerEvents, disablePointerEventsProp]);
  var registerOnParent = useContext(NestedHovercardContext); // Register the hovercard as a nested hovercard on the parent hovercard if
  // if it's not a modal, is portal and is mounted. We don't need to register
  // non-portal hovercards because they will be captured by the contains
  // function in the isMovingOnHovercard function above. This must be a layout
  // effect so we don't lose mouse move events right after the nested
  // hovercard has been mounted (for example, a submenu that's overlapping its
  // menu button and we keep moving the mouse while the submenu is due to
  // open).

  useSafeLayoutEffect(function () {
    if (modal) return;
    if (!portal) return;
    if (!state.mounted) return;
    if (!domReady) return;
    var element = ref.current;
    if (!element) return;
    return registerOnParent == null ? void 0 : registerOnParent(element);
  }, [modal, portal, state.mounted, domReady]);
  var registerNestedHovercard = useCallback(function (element) {
    setNestedHovercards(function (prevElements) {
      return [].concat(_toConsumableArray(prevElements), [element]);
    });
    var parentUnregister = registerOnParent == null ? void 0 : registerOnParent(element);
    return function () {
      setNestedHovercards(function (prevElements) {
        return prevElements.filter(function (item) {
          return item !== element;
        });
      });
      parentUnregister == null ? void 0 : parentUnregister();
    };
  }, [registerOnParent]);
  props = useWrapElement(props, function (element) {
    return /*#__PURE__*/jsx(NestedHovercardContext.Provider, {
      value: registerNestedHovercard,
      children: element
    });
  }, [registerNestedHovercard]);
  props = _objectSpread(_objectSpread({}, props), {}, {
    ref: useForkRef(ref, props.ref)
  });
  props = useAutoFocusOnHide(_objectSpread({
    state: state
  }, props));
  props = useAutoFocusOnShow(_objectSpread({
    state: state
  }, props));
  props = usePopover(_objectSpread(_objectSpread({
    hideOnEscape: hideOnEscape,
    state: state,
    modal: modal,
    portal: portal
  }, props), {}, {
    portalRef: portalRef
  }));
  return props;
});
/**
 * A component that renders a hovercard element, which is a popover that's
 * usually made visible by hovering the mouse cursor over an anchor element
 * (`HovercardAnchor`).
 * @see https://ariakit.org/components/hovercard
 * @example
 * ```jsx
 * const hovercard = useHovercardState();
 * <HovercardAnchor state={hovercard}>@username</HovercardAnchor>
 * <Hovercard state={hovercard}>Details</Hovercard>
 * ```
 */

var Hovercard = createComponent(function (props) {
  var htmlProps = useHovercard(props);
  return createElement("div", htmlProps);
});
export { Hovercard, useHovercard };